import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
  OnInit,
} from '@angular/core';
import { RestoreTaskService } from '../../../../core/services/restore-task.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import moment from 'moment';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  switchMap,
  tap,
  map,
  shareReplay,
} from 'rxjs';
import { WebsiteData } from '../../../../shared/models/website-overview.model';
import { AsyncPipe, DatePipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { DatePickerComponent } from '../../../../shared/components/date-picker/date-picker.component';
import { StatusLabelComponent } from '../../../../shared/components/status-label/status-label.component';
import { TableComponent } from '../../../../shared/components/table/table.component';
import {
  RestoreTaskSummaryDTO,
  TaskProgressStatus,
} from '../../../../shared/models/backups.models';
import { Status } from '../../../../shared/models/status-label.model';
import { RouterLink } from '@angular/router';
import { DateRangeValue } from '../../../../shared/components/date-picker/date-picker.models';

@Component({
  selector: 'app-restores',
  imports: [
    MatCardModule,
    FontAwesomeModule,
    MatSlideToggleModule,
    TableComponent,
    StatusLabelComponent,
    MatTableModule,
    MatTooltipModule,
    AsyncPipe,
    MatButtonModule,
    DatePickerComponent,
    ReactiveFormsModule,
    DatePipe,
    RouterLink,
  ],
  templateUrl: './restores.component.html',
  styleUrl: './restores.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RestoresComponent implements OnInit {
  private restoreTaskService = inject(RestoreTaskService);

  websiteData = input.required<WebsiteData>();

  website = computed(() => this.websiteData().website);

  website$ = toObservable(this.website);

  refresh$ = new BehaviorSubject<void>(undefined);

  filters = signal<{
    page: number;
    pageSize: number;
    searchText?: string;
    timestampFrom?: number;
    timestampTo?: number;
  }>({
    page: 1,
    pageSize: 10,
  });

  filters$ = toObservable(this.filters);

  dateRange = new FormControl<DateRangeValue | null>(null);

  allRestoresCount = signal(0);

  restores$ = combineLatest([this.website$, this.filters$.pipe(debounceTime(300))]).pipe(
    switchMap(([website, filters]) =>
      this.refresh$.pipe(
        switchMap(() =>
          this.restoreTaskService.searchRestoreTasks({ ...filters, websiteId: website.id }),
        ),
      ),
    ),
    tap(({ total }) => this.allRestoresCount.set(total)),
    map(({ data }) => data ?? []),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  restoresColumns = [
    {
      columnDef: 'id',
      header: 'ID',
      cell: (element: RestoreTaskSummaryDTO) => element.id.toString(),
    },
    {
      columnDef: 'backupType',
      header: 'Backup Type',
      cell: (element: RestoreTaskSummaryDTO) =>
        element.backup?.scope === 'server' ? 'Server Backup' : 'Terminated Backup',
    },
    {
      columnDef: 'status',
      header: 'Status',
      cell: () => '',
      useTemplate: true,
    },
    {
      columnDef: 'runtime',
      header: 'Runtime',
      cell: (
        element: RestoreTaskSummaryDTO,
      ) => `Finished: ${element.endDate ? moment(element.endDate).format('DD.MM.YYYY, hh:mm:ss') : 'N/A'}
       Start: ${moment(element.startDate).format('DD.MM.YYYY, hh:mm:ss')}`,
      useTemplate: true,
    },
    {
      columnDef: 'buttons',
      header: '',
      cell: () => '',
      useTemplate: true,
    },
  ];

  ngOnInit(): void {
    this.dateRange.valueChanges.subscribe((value: DateRangeValue | null) => {
      if (value) {
        const start = value.start?.valueOf();
        const end = value.end?.valueOf();

        if (start && end)
          this.filters.update((f) => ({ ...f, timestampFrom: start, timestampTo: end }));
      }
    });
  }

  onSearch(text: string) {
    this.filters.update((f) => ({ ...f, searchText: text }));
  }

  handlePageEvent(event: PageEvent) {
    this.filters.update((f) => ({ ...f, page: event.pageIndex + 1, pageSize: event.pageSize }));
  }

  clearFilters(): void {
    this.filters.update((f) => ({ ...f, timestampFrom: undefined, timestampTo: undefined }));
    this.dateRange.reset();
  }

  toStatus(key: TaskProgressStatus): Status {
    switch (key) {
      case TaskProgressStatus.Finished:
        return Status.FINISHED;
      case TaskProgressStatus.Pending:
        return Status.PENDING;
      case TaskProgressStatus.Running:
        return Status.IN_PROGRESS;
      default:
        return Status.FAILED;
    }
  }
}
